// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Badge, Modal } from '@loa/ui-components';
import type { NewBusinessTemplate } from '../../api/newBusinessTemplateApi';

type Props = Readonly<{ open: boolean; template: NewBusinessTemplate; onClose: () => void }>;

/**
 * Read-only view of a New Business Application template — the advisor-facing
 * "View" for both platform defaults and brokerage overrides. Authoring
 * (upload/annotate) lives in the Admin UI; brokerage-override authoring in the
 * Advisor UI is a documented follow-up.
 */
export function NewBusinessTemplateViewModal({ open, template, onClose }: Props) {
  const sections = Array.isArray((template.paramSchema as { sections?: unknown[] })?.sections)
    ? ((template.paramSchema as { sections?: unknown[] }).sections as unknown[]).length
    : 0;
  const rows: Array<[string, string]> = [
    ['Provider', template.providerName ?? template.providerId],
    ['Product', template.productName ?? template.productId],
    ['Category', template.category ?? '—'],
    ['Currency', template.currencyCode],
    ['Scope', template.scope === 'PLATFORM' ? 'Platform default' : 'Brokerage override'],
    ['Provider form PDF', template.hasPdf ? 'Uploaded' : 'Not uploaded'],
    ['Signature regions', String(template.signatureRegionCount ?? 0)],
    ['Bindings', String(template.annotationMap?.length ?? 0)],
    ['Parameter sections', String(sections)],
    ['Status', template.isActive ? 'Active' : 'Inactive'],
  ];

  return (
    <Modal open={open} onClose={onClose} size="medium">
      <Modal.Header
        icon="document"
        title={`${template.productName ?? 'New Business template'} — ${
          template.scope === 'PLATFORM' ? 'Platform' : 'Your brokerage'
        }`}
        onClose={onClose}
      />
      <Modal.Content>
        {template.notes && <p className="text-body-03 text-grey-02">{template.notes}</p>}
        <dl className="flex flex-col gap-2" data-testid="nb-template-view">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 text-body-03">
              <dt className="text-grey-02">{label}</dt>
              <dd className="text-grey-01">
                {label === 'Signature regions' && (template.signatureRegionCount ?? 0) === 0 ? (
                  <Badge color="grey" variant="outlined">none</Badge>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
        </dl>
      </Modal.Content>
      <Modal.Actions secondaryAction={{ label: 'Close', onClick: onClose }} />
    </Modal>
  );
}
